import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Outlet, useLocation } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AdSensePlacement from '../components/ads/AdSensePlacement';

export default function RootLayout() {
  const location = useLocation();
  const [adPortal, setAdPortal] = useState<{ element: HTMLElement; position: 'guide_detail' | 'calculator_detail' | 'compare_detail' } | null>(null);

  useEffect(() => {
    // Scroll to top on route change to maintain clean, professional SPA feel
    window.scrollTo(0, 0);

    // Clean up old dynamic containers on path change
    setAdPortal(null);
    const existing = document.getElementById('dynamic-adsense-target');
    if (existing) {
      existing.remove();
    }

    // Wait for the DOM of the navigated page to settle
    const timer = setTimeout(() => {
      const path = location.pathname;

      if (path.startsWith('/calculators/') && path !== '/calculators/' && path !== '/calculators') {
        // Individual calculator pages - Insert before comprehensive SEO educational notes block
        const seoSections = Array.from(document.querySelectorAll('section'));
        const seoSec = seoSections.find(sec => {
          const h2 = sec.querySelector('h2');
          return h2 && (h2.textContent?.toLowerCase().includes('what is') || h2.textContent?.toLowerCase().includes('understanding') || h2.textContent?.toLowerCase().includes('how does'));
        }) || document.querySelector('section.bg-white') || document.querySelector('.grid.grid-cols-1.lg:grid-cols-12')?.nextElementSibling;
        
        if (seoSec && seoSec.parentNode) {
          const container = document.createElement('div');
          container.id = 'dynamic-adsense-target';
          container.className = 'w-full my-6';
          seoSec.parentNode.insertBefore(container, seoSec);
          setAdPortal({ element: container, position: 'calculator_detail' });
        }
      } else if (path.startsWith('/guides/') && !['/guides', '/guides/', '/guides/banking', '/guides/savings', '/guides/loans', '/guides/credit-cards'].includes(path)) {
        // Individual guide pages - Insert prior to FAQs
        const article = document.querySelector('article');
        if (article) {
          const faqSection = Array.from(article.querySelectorAll('section')).find(sec => {
            const h2 = sec.querySelector('h2');
            return h2 && (h2.textContent?.toLowerCase().includes('frequently') || h2.textContent?.toLowerCase().includes('faq'));
          });
          
          const container = document.createElement('div');
          container.id = 'dynamic-adsense-target';
          container.className = 'w-full my-6';
          if (faqSection && faqSection.parentNode) {
            faqSection.parentNode.insertBefore(container, faqSection);
          } else {
            // Append near end of article if FAQ section is not found
            article.appendChild(container);
          }
          setAdPortal({ element: container, position: 'guide_detail' });
        }
      } else if (path.startsWith('/compare/') && path !== '/compare' && path !== '/compare/') {
        // Individual compare pages - Insert after Comparison Tables (.overflow-x-auto or table)
        const tableContainer = document.querySelector('.overflow-x-auto') || document.querySelector('table');
        
        if (tableContainer && tableContainer.parentNode) {
          const container = document.createElement('div');
          container.id = 'dynamic-adsense-target';
          container.className = 'w-full my-6';
          tableContainer.parentNode.insertBefore(container, tableContainer.nextSibling);
          setAdPortal({ element: container, position: 'compare_detail' });
        } else {
          // Fallback if table not found
          let targetElement = Array.from(document.querySelectorAll('h2, h3')).find(el => {
            return el.textContent?.toLowerCase().includes('choose') || el.textContent?.toLowerCase().includes('explore calculators');
          });
          
          if (!targetElement) {
            targetElement = document.querySelector('div.bg-slate-50.border.border-slate-300') || undefined;
          }

          if (targetElement && targetElement.parentNode) {
            const container = document.createElement('div');
            container.id = 'dynamic-adsense-target';
            container.className = 'w-full my-6';
            targetElement.parentNode.insertBefore(container, targetElement);
            setAdPortal({ element: container, position: 'compare_detail' });
          }
        }
      }
    }, 120);

    return () => {
      clearTimeout(timer);
      const existing = document.getElementById('dynamic-adsense-target');
      if (existing) {
        existing.remove();
      }
    };
  }, [location.pathname]);

  return (
    <div id="app-viewport" className="flex min-h-screen flex-col bg-slate-50/50 text-slate-800 antialiased font-sans">
      <div className="print:hidden">
        <Header />
      </div>
      <main id="main-content-flow" className="grow">
        <Outlet />
      </main>
      <div className="print:hidden">
        <Footer />
      </div>
      {adPortal && createPortal(
        <div className="print:hidden">
          <AdSensePlacement position={adPortal.position} />
        </div>,
        adPortal.element
      )}
    </div>
  );
}
